import { FC } from 'react';
import UniversalLink from 'src/common/components/UniversalLink';
import {
  SUPPORT_URLS, NEW_TAB_ATTRIBUTES, GMB_SUPPORT_EMAIL, PATHS,
} from 'src/common/constants';
import styles from 'src/content/ContentPages.module.scss';

const PrivacyPolicy: FC = () => (
  <main className={styles.root}>
    <h1 className={styles.title}>Privacy Policy</h1>
    <h2 className={styles.subTitle}>EFFECTIVE FEBRUARY 7 2023</h2>

    <p>
      This is the Privacy Notice (the “Privacy Notice”) of Getmyboat, Inc. d/b/a Getmyboat
      (“Getmyboat,” “we,” or “us”), whose corporate headquarters is located at 101 International
      Parkway, Adairsville, Georgia 30103. This Privacy Notice sets out how Getmyboat collects and
      processes your Personal Data (as defined below) when you access and use the services provided
      by Getmyboat (the “Services”), including (but not limited to) the use of our website at
      www.getmyboat.com (the “Site” or “Website”), and lists certain of your rights in relation to
      your Personal Data under applicable law.
    </p>

    <p>
      This Privacy Notice relates to data about you and your interaction with our Services.
      “Personal Data,” for purposes of this Privacy Notice, means any information relating to an
      identified or identifiable natural person (each such person, a “data subject”). An
      identifiable natural person is one who can be identified, directly or indirectly, in
      particular by reference to an identifier such as a name, an identification number, location
      data, an online identifier or to one or more factors specific to the physical, physiological,
      genetic, mental, economic, cultural or social identity of that natural person.
    </p>

    <h2>Data We Collect</h2>
    <h3>Data You Provide To Us</h3>
    <h4>Registration</h4>
    <p>
      We collect personal data when you use or interact with our Site and Services, including when
      you register with us. To create an account with us, you will need to provide personal data
      including your name, email address, and a password.
    </p>

    <h4>Interaction with our Site and Making Purchases</h4>
    <p>
      We collect personal data when you use or interact with our Site and Services, including when
      you browse our products online or make purchases from us. In order to book a trip through
      Getmyboat, or to provide a trip as a boat owner and receive payment, you will need to provide
      payment and billing information.
    </p>

    <h4>Profile</h4>
    <p>
      You have choices about the information you provide for your profile, such as a photo, spoken
      languages, and a short biography. You don&apos;t have to provide additional information on
      your profile; however, profile information helps you to get more from our Services.
    </p>
    <p>
      Other than the personal information required to create an account (i.e., your name, email
      address, phone number and password), it is your choice whether to include additional personal
      data on your profile. We do not require you to provide Sensitive Personal Data to complete
      your profile. We recommend that you exercise care in deciding what information and
      content you wish to disclose on the areas of our Site that are accessible to the general
      public. Please do not post or add personal data to your profile that you would not want to be
      publicly available.
    </p>
    <h5>Digest</h5>
    <p className={styles.digest}>
      You create your profile (a complete profile helps you get the most from our Services).
      You don’t have to post or upload personal data through our Site; though if you don’t, it may
      limit your ability to connect with Owners and Renters over our Services.
    </p>
    <h5>Digest</h5>
    <p className={styles.digest}>
      You give other data to us, such as by adding boat listings and posting pictures on reviews.
    </p>

    <h3>Data From Others</h3>
    <h4>User-Submitted Content</h4>
    <p>
      You and others may post content that includes information about you (e.g., as part of reviews
      of boats or renters) on our Services.
    </p>

    <h4>Service Use</h4>
    <p>
      We log usage data when you visit or otherwise use our Services, including our sites and apps,
      such as when you view or click on content (e.g. boat listings), perform a search, or install
      or update one of our mobile apps. We use log-ins, cookies, device information and internet
      protocol (&quot;IP&quot;) addresses to identify you and log your use.
    </p>
    <h5>Digest</h5>
    <p className={styles.digest}>
      We log your visits and use of our Services, including mobile apps.
    </p>

    <h4>Cookies</h4>
    <p>
      As further described in
      {' our '}
      <UniversalLink
        href={PATHS.COOKIES_POLICY}
        className={styles.link}
      >
        Cookies Policy
      </UniversalLink>
      , we use cookies and similar technologies (e.g. pixels and device identifiers) to recognize
      you and/or your device(s) when using our Services across different and devices. We also allow
      certain third parties to use cookies as described in our Cookie Policy. For information
      regarding your choices with respect to cookies, please see the Lawful Bases for Processing
      section and our Cookie Policy.
    </p>
    <h5>Digest</h5>
    <p className={styles.digest}>We collect data through cookies and similar technologies.</p>

    <h4>Your Device and Location</h4>
    <p>
      When you visit our Services, we receive the URL of the site you came from. We also get
      information about your IP address, proxy server, operating system, web browser and add-ons,
      device identifier and features, and/or ISP or your mobile carrier. If you use our Services
      from a mobile device, that device will send us data about your approximate location based on
      your phone settings. We will ask you to opt-in (i.e., consent) before we collect your precise
      location.
    </p>
    <h5>Digest</h5>
    <p className={styles.digest}>
      We receive data from your devices and networks, including location data.
    </p>

    <h4>Communications Through Our Services and Promotional Materials</h4>
    <p>
      We collect personal data when you communicate with us or sign up to receive promotional
      materials or information, including email address and phone number. In addition, we collect
      information about you when you send, receive, or engage with messages in connection with our
      Services. For example, if you get a Booking Inquiry or Offer, we track whether you have acted
      on it and will send you reminders to take further actions.
    </p>
    <p>
      We also collect personal data from you when you provide, post or upload it to our Site or via
      our Services, such as when you submit a form (e.g., a Boat listing) or upload a review photo
      to our Site.
    </p>
    <h5>Digest</h5>
    <p className={styles.digest}>
      We collect information from you when you communicate through our Services.
    </p>

    <h4>Third Parties</h4>
    <p>
      We may collect information, such as name and email address, from publicly available sources
      and other third parties, such as marketing data providers, to help us provide and improve the
      Services and for marketing and advertising. We may combine your personal data with data we
      obtain from our Services, other users, or third parties to enhance your experience and improve
      the Services.
    </p>

    <h4>Users Under 16 Years of Age</h4>
    <p>
      We do not knowingly collect personal data online from individuals under 16 years without
      parental consent. If you become aware that an individual under 16 years of age has provided us
      with personal data without parental consent, please contact the data protection officer
      {' at '}
      <a
        href="mailto:dpo@getmyboat.com"
        className={styles.link}
        {...NEW_TAB_ATTRIBUTES}
      >
        dpo@getmyboat.com
      </a>
      . If we become aware that an individual under 16 years has provided us with personal data
      without parental consent, we will take steps to remove the data as permitted by law.
    </p>

    <h2>How We Use Your Data</h2>
    <p>
      We use your personal data as disclosed in this Privacy Notice. How we use your personal data
      may depend, in part, on how you use our Services and the choices you make in your settings. We
      use the data that we have about you to provide, improve, and personalize, including with the
      help of automated systems and inferences we make, our Services so that they can be more
      relevant and useful to you and others.
    </p>
    <h5>Digest</h5>
    <p className={styles.digest}>
      We use your data to provide, support, personalize and develop our Services.
    </p>

    <h3>Account Administration and Provision of Services</h3>
    <p>
      We use personal data to administer your account, including to verify your identity and to
      authenticate and authorize access to our Services. For example, we may collect user
      photographs as an additional security measure for identity verification and fraud-prevention
      purposes.
    </p>
    <h5>Digest</h5>
    <p className={styles.digest}>
      Our Services help you book water experiences and list water experiences for other users to
      book.
    </p>

    <h4>Boat Listing</h4>
    <p>
      Our Services help connect you with potential customers to complete bookings for your boat
      rental or water experience business that you offer through our Site. To do so, you may choose
      to publish a listing on our Site with a description of what you offer, and interested
      customers will send you a booking inquiry via our Site. When customers send you a booking
      inquiry, you will be able to communicate with each other through custom and automated
      (triggered by selecting a response action) messages to discuss further details and execute
      common booking-related actions. Customers can confirm the booking and make a payment through
      our Site.
    </p>
    <p>
      We will use data about you and your listing to help others find your listing, and sometimes to
      promote your listing and our Services. When a customer confirms a booking with you, we will
      provide your contact information to them to facilitate communication outside our platform as
      desired or necessary. We will also use your personal data in order to provide payment for a
      booking for your boat rental or water experience business offering.
    </p>

    <h4>Boat Rental</h4>
    <p>
      Our Services allow you to find and book boat rentals and water experiences. We may use data we
      have about you and data you provide to show you listings that might match your interests, and
      we may send details regarding your booking inquiry to listing owners.
    </p>
    <p>
      When you send a booking inquiry, you and the listing owner will be able to communicate with
      each other through custom and automated (triggered by selecting a response action) messages to
      discuss further details and execute common booking-related actions. You can confirm the
      booking and make a payment through our Site.
    </p>
    <p>
      When you confirm a booking, we will provide your contact information to the listing owner to
      facilitate communication outside our platform as desired or necessary.
    </p>

    <h3>Communications and Marketing</h3>
    <p>
      We may contact you through email, mobile phone, notices posted on our websites or apps,
      messages to your Getmyboat inbox, and other ways through our Services, including text messages
      and push notifications. We will send you messages about the availability of our Services,
      security, or other service-related issues. We may also send messages about how to use the
      Services, reminders related to rental or booking inquiries and requests. We may also use your
      personal information to send you promotional messages from us.
    </p>
    <p>
      We also enable communications between you and others through our Services, including Booking
      Inquiries, Offers, and messages.
    </p>
    <p>
      We may also use personal data to send invitations and communications promoting our Services.
    </p>
    <p>
      You may change your communication preferences at any time by visiting the account settings
      section of the Site. Please be advised that you may not be able to opt-out of receiving
      certain service or transactional messages from us, including security and legal notices, and
      communications related to the provision of the Services.
    </p>
    <p>
      No mobile information will be shared with third parties/affiliates for marketing/promotional
      purposes. All the above categories exclude text messaging originator opt-in data and consent;
      this information will not be shared with any third parties.
    </p>
    <h5>Digest</h5>
    <p className={styles.digest}>
      We contact you and enable communications between Members. We offer settings to control what
      messages you receive and how often you receive some types of messages.
    </p>

    <h3>Developing Services and Research</h3>
    <h4>Service Development</h4>
    <p>
      We use anonymized data, including public feedback, for analytics purposes and to conduct
      research and development for the further development and improvement of our Services in order
      to provide you and others with a better, more intuitive and personalized experience, drive
      membership growth and engagement on our Services, and help connect Owners and Renters to each
      other and to economic opportunity.
    </p>

    <h4>Customer Support</h4>
    <p>
      We use the data (which can include your communications) to investigate, respond to and resolve
      inquiries, complaints and Service issues.
    </p>
    <h5>Digest</h5>
    <p className={styles.digest}>We use data to respond to requests and maintain the Services.</p>

    <h4>Aggregate Insights</h4>
    <p>
      We may create de-identified or anonymous data from personal data by removing data components
      (such as your name, email address, or linkable tracking ID) that makes the data personally
      identifiable to you or through obfuscation or through other means, including for the purpose
      of producing and sharing aggregated insights that do not identify you. For example, we may use
      your data to generate statistics about our members and boats on our Services. Our use of
      anonymized data is not subject to this Privacy Policy.
    </p>
    <h5>Digest</h5>
    <p className={styles.digest}>We use de-identified data to generate aggregate insights.</p>

    <h4>Crime-Prevention and Legal Purposes</h4>
    <p>
      We use your data if we believe in good faith it’s necessary for security purposes or to
      investigate possible fraud or other violations of our User Agreement or this Privacy Policy,
      and/or attempts to harm our Members or Visitors, and for other legal purposes, such as to
      comply with applicable laws and respond to legal requests.
    </p>
    <h5>Digest</h5>
    <p className={styles.digest}>We use data for security, fraud prevention, and legal purposes.</p>

    <h2>How We Share Information</h2>
    <h3>Our Services</h3>
    <h4>Public User Profile</h4>
    <p>
      The information you provide for your public user profile is fully visible to anybody who
      visits the Site.
    </p>

    <h4>Listings, Wishlists, and Reviews</h4>
    <p>
      Our Site allows users to view and share personal data, including through listings, wishlists,
      and reviews.
    </p>
    <ul>
      <li>
        When you create a boat listing, all information you provide in connection with the listing
        will be publicly viewable, including headline, description, and listing photos.
      </li>
      <li>
        When you create a wishlist, unless you make the wishlist private, it will be viewable by any
        Member or Visitor that has the link to the wishlist.
      </li>
      <li>
        When you submit a review (e.g., text and photos) publicly on our Site, it can be viewed by
        the general public. Members, Visitors and others will be able to find and see your
        publicly-shared content, including your name (and photo if you have provided one).
      </li>
    </ul>
    <h5>Digest</h5>
    <p className={styles.digest}>
      Any data that you include on your profile and any content you publicly post on our
      Services may be seen by others.
    </p>

    <h3>Service Providers</h3>
    <p>
      We use other third parties to help us provide our Services (e.g., authentication, maintenance,
      analysis, audit, payments, fraud detection, marketing and development) as permitted by law.
      Those third parties may have access to your information as reasonably necessary to perform
      these tasks on our behalf, and they are obligated not to disclose or use your personal data
      for other purposes.
    </p>
    <h5>Digest</h5>
    <p className={styles.digest}>
      We may use others to assist us in the provision of our Services.
    </p>

    <h3>Legal Disclosures</h3>
    <p>
      It is possible that we may need to disclose information about you when required by law,
      subpoena, or other legal process, or where we have a good faith belief that disclosure is
      reasonably necessary to (1) investigate, prevent, or take action regarding suspected or actual
      illegal or wrongful activities or to assist government agencies; (2) enforce our agreements
      with you or with third parties; (3) investigate and defend our rights and property, including
      against any third-party claims or allegations; (4) protect the security or integrity of our
      Services; or (5) exercise or protect the rights and/or safety of Getmyboat, our Members,
      personnel, or other third parties.
    </p>
    <h5>Digest</h5>
    <p className={styles.digest}>
      We may need to share your data when we believe it’s required by law or to help protect the
      rights and safety of you, us or others.
    </p>

    <h3>Change in Control or Sale</h3>
    <p>
      We may also share your personal data as part of a sale, merger or change in control, or in
      preparation for any of these events. Any other entity which buys us or part of our may
      continue to use your data only in the manner consistent with this Privacy Policy unless you
      agree otherwise.
    </p>
    <h5>Digest</h5>
    <p className={styles.digest}>
      We may share your data if our business is sold to others, in accordance with this Privacy
      Policy.
    </p>

    <h2>Your Rights, Choices &amp; Obligations</h2>
    <h3>Data Retention</h3>
    <p>
      We retain your personal data for as long as you maintain your account, as needed to provide
      you the Services, or as otherwise required by law. This includes data you or others provided
      to us and data generated or inferred from your use of our Services, as described in this
      Privacy Policy. For example, if you use our Services when looking for a boat every few years,
      we will retain your personal data and keep your profile open in order to provide you the
      Services until you decide to close your account (as described in the Account Closure section
      of this Privacy Policy), or otherwise request that we remove your personal data as described
      in this Privacy Policy.
    </p>
    <h5>Digest</h5>
    <p className={styles.digest}>
      We keep most of your personal data for as long as your account is open.
    </p>

    <h3>Rights to Access and Control Your Personal Data</h3>
    <p>
      We provide many choices about the collection, use and sharing of your data, from deleting or
      correcting data you include in your profile and controlling the visibility of your boats to
      advertising opt-outs and communication controls.
    </p>
    <p>
      You have the right in certain circumstances to request confirmation from us as to whether or
      not we are processing your personal data. For personal data that we have about you:
    </p>
    <ul>
      <li>
        <strong>Delete Data:</strong>
        {' '}
        You can ask us to erase or delete all or some of your personal
        data (e.g., if it is no longer necessary to provide Services to you).
      </li>
      <li>
        <strong>Change or Correct Data:</strong>
        {' '}
        You can edit some of your personal data through
        your account. You can also ask us to change, update or correct your data in certain cases,
        for example, if your information inaccurate.
      </li>
      <li>
        <strong>Object to, or Limit or Restrict, Use of Data:</strong>
        {' '}
        You can ask us to stop using
        all or some of your personal data (e.g., if we have no legal right to keep using it) or to
        limit our use of it (e.g., if your personal data is inaccurate or unlawfully held).
      </li>
      <li>
        <strong>Right to Access and/or Take Your Data:</strong>
        {' '}
        You can ask us for a copy of your
        personal data and can ask for a copy of personal data you provided in machine readable form,
        or to transmit such data to another controller.
      </li>
    </ul>
    <p>
      You may also exercise your rights by contacting us at the contact information below. As
      permitted by law, certain data elements may not be subject to access, modification,
      portability, restriction, and/or deletion. Furthermore, where permissible, we may charge for
      this service. We will respond to reasonable requests as soon as practicable and as required by
      law. If you have questions or concerns about these terms, please contact our Data Protection
      {'Officer at '}
      <a
        href="mailto:dpo@getmyboat.com"
        className={styles.link}
      >
        dpo@getmyboat.com
      </a>
      .
    </p>
    <h5>Digest</h5>
    <p className={styles.digest}>
      You can access or delete your personal data. You have choices about how your data is
      collected, used and shared.
    </p>

    <h3>Account Closure</h3>
    <p>
      {'If you wish to close your Getmyboat account, please contact our '}
      <a
        href={SUPPORT_URLS.REQUEST}
        className={styles.link}
        {...NEW_TAB_ATTRIBUTES}
      >
        Support Team
      </a>
      . Your personal data will generally stop being publicly visible to others on our Site within
      72 hours from when we receive your account closure request. We generally delete closed account
      information from our systems within 30 days of receiving your account closure request, except
      as noted in this Privacy Policy.
    </p>
    <p>
      We may retain your personal data after you have closed your account if reasonably necessary to
      comply with our legal obligations (including law enforcement requests), meet regulatory
      requirements, resolve disputes, maintain security, prevent fraud and abuse, enforce our User
      Agreement, or fulfill your request to “unsubscribe” from further messages from us, or for
      other legal purposes. We may retain anonymized information after your account has been closed.
    </p>
    <p>
      Please note that information you have shared with others (e.g., through posting a review on
      our Site) may remain visible after you close your account or delete the information from your
      own profile or mailbox, so we recommend that you exercise care in deciding what information
      and content you wish to disclose on the areas of our Site that are accessible to the general
      public. Review content associated with closed accounts will show an unknown user as the source
      of the review. Your profile may continue to be displayed in the services of others (e.g.,
      search engine results) until those third parties refresh their cache.
    </p>
    <h5>Digest</h5>
    <p className={styles.digest}>We may keep certain data after you close your account.</p>

    <h2>Other Important Information</h2>
    <h3>Security and Links to Third-Party Sites</h3>
    <p>
      We implement technical and organizational security safeguards designed to protect your
      personal data, such as HTTPS. We regularly monitor our systems for potential vulnerabilities.
      However, please be advised that we cannot fully eliminate security risks associated with the
      storage and transmission of personal data.
    </p>
    <p>
      This Privacy Notice only applies to Getmyboat. Our Site or Services may provide a link or
      otherwise provide access to another website, mobile application, or Internet location
      (collectively “Third-Party Sites”). We provide these links merely for your convenience. We
      have no control over, do not review, and are not responsible for Third-Party Sites, their
      content, or any goods or services available through the Third-Party Sites. Our Privacy Policy
      does not apply to Third-Party Sites, and any data you provide to Third-Party Sites, you
      provide at your own risk. We encourage you to review the privacy policies of any Third-Party
      Sites with which you choose to interact.
    </p>
    <h5>Digest</h5>
    <p className={styles.digest}>
      We implement technical and organizational security safeguards designed to protect your
      personal data.
    </p>

    <h3>Cross-Border Data Transfers</h3>
    <p>We may process and store your data outside of your country.</p>
    <p>
      We process data both inside and outside of the United States and rely on legally-provided
      mechanisms to lawfully transfer data across borders as required by applicable law. Countries
      where we process data may have laws which are different, and potentially not as protective, as
      the laws of your own country. By using our Services and/or submitting your personal data, you
      agree to the transfer, storage, and/or processing of your personal data in the United States.
    </p>

    <h3>Lawful Bases for Processing</h3>
    <p>
      We will only collect and process personal data about you where we have lawful bases. In some
      cases, we will ask for your consent so that we may process your personal data. However, in
      certain circumstances, applicable data protection laws allow us to process your personal data
      without needing to obtain your consent. For example, in certain cases, separate consent is not
      required, including:
    </p>
    <ul>
      <li>
        <strong>For the performance of a contract.</strong>
        {' '}
        To perform our contractual obligations
        to you, including our fulfilling orders or purchases you have made, contacting you in
        relation to any issues with your order or use of our Services, in relation to the provision
        of our Services, or where we need to provide your personal data to our service providers
        related to the provision of the Services, including for account activation and management.
      </li>
      <li>
        <strong>To comply with legal obligations.</strong>
        {' '}
        To comply with laws, regulators, court
        orders, or other legal obligations, or pursuant to legal process.
      </li>
      <li>
        <strong>Legitimate Interests.</strong>
        {' '}
        To operate our business and provide our Services
        (other than in performing our contractual obligations to you) for our “legitimate interests”
        and for the purposes of applicable law, except where such interests are overridden by the
        interests or fundamental rights and freedoms of the data subject which require protection of
        personal data. Legitimate interests may include:
        <ul>
          <li>
            To communicate with you regarding the Services and to address and respond to your
            requests, inquiries, and complaints.
          </li>
          <li>For our direct marketing purposes.</li>
          <li>To send you surveys in connection with our Services.</li>
          <li>
            To assist in the investigation of suspected illegal or wrongful activity, and to protect
            and defend our rights and property, or the rights or safety of third parties.
          </li>
          <li>For fraud, loss, and other crime prevention purposes.</li>
          <li>To develop, provide, and improve our Services.</li>
          <li>
            To enforce our Terms of Use, License Agreements, or this Privacy Notice, or agreements
            with third parties.
          </li>
        </ul>
      </li>
      <li>
        <strong>To protect data subjects’ vital interests.</strong>
        To protect the vital interests of you or of another person.
      </li>
    </ul>
    <p>
      In cases where we are not already authorized to process the personal data under applicable
      law, we may ask for your consent to process your Personal Data, including:
    </p>
    <ul>
      <li>
        <strong>Marketing.</strong>
        {' '}
        We may ask for your consent for Getmyboat, Inc. d/b/a Getmyboat
        and Getmyboat.com or our partners or vendors to contact you by telephone, SMS, post and/or
        email about other offers, products, promotions, developments or Services which we think may
        be of interest to you and for other marketing purposes.
      </li>
      <li>
        <strong>Precise Location Data.</strong>
        {' '}
        We may request your consent to collect your precise
        location. Our Services may require this data to provide the Services. If you choose not to
        provide this information, certain Services or features may be unavailable or not function
        properly.
      </li>
      <li>
        <strong>Use of Cookies.</strong>
        {' '}
        As further described in our
        {' '}
        <UniversalLink
          href={PATHS.COOKIES_POLICY}
          className={styles.link}
        >
          Cookies Policy
        </UniversalLink>
        , our Site uses cookies and similar technologies to improve user experience and for
        analytics. We also use cookies and similar technologies for purposes of marketing and
        advertising, including interest-based advertising. For more information on interest-based
        advertising, see the Interest-Based Advertising section. In those jurisdictions that require
        it, we allow the data subject to give explicit permission or to deny the use of certain
        categories of cookies.
      </li>
    </ul>
    <p>
      A “cookie” is a small text file that a web server stores in browser software. A browser sends
      cookies to a server when the browser makes a connection to the server (for example, when
      requesting a web page from the same domain that created the cookie). The purpose of cookies is
      to remember the browser over time and distinguish one browser instance (or user) from all
      others. Some cookies and other technologies may serve to track Personal Data previously
      entered by a web user on our site. Most browsers allow you to control cookies, including
      whether or not to accept them, and how to remove them. Cookies can remember login information,
      preferences, and similar information.
    </p>
    <p>
      Cookies, as well as other tracking technologies such as HTML5 local storage, and Local Shared
      Objects (such as “Flash” cookies), and similar mechanisms, may record information such as a
      unique identifier, information you enter in a form, IP address, and other categories of data.
    </p>
    <p>
      We also use web beacons or “pixels,” and in certain circumstances may collect IP address,
      screen resolution and browser software and operating system types; clickstream patterns; dates
      and times that our site is accessed, and other categories of data.
    </p>
    <p>
      If you want to block the use and saving of cookies from the Site on to their computers hard
      drive, you should take the necessary steps within your web browser’s settings to block all
      cookies from the Site and its external serving vendors. Please note that if you choose to
      erase or block your cookies, certain parts of our Site may not function correctly. For
      information on how to disable cookies, refer to your browser’s documentation.
    </p>
    <p>
      Where we rely on your consent to process personal data, you have the right to withdraw or
      decline your consent at any time, provided that we are not required by applicable law or
      professional standards to retain such information. Please note, however, that if you do not
      provide consent or if you withdraw your consent, we may be unable to provide you certain
      Services that require the use of personal data.
    </p>
    <p>
      If you want to stop receiving future marketing messages and materials, you can do so by
      clicking the “unsubscribe” link included in our email marketing messages or by contacting us
      {' at '}
      <a
        href={`mailto:${GMB_SUPPORT_EMAIL}`}
        className={styles.link}
        {...NEW_TAB_ATTRIBUTES}
      >
        {GMB_SUPPORT_EMAIL}
      </a>
      .
    </p>
    <p>
      Where we rely on legitimate interests, you have the right to object. If you have any questions
      about the lawful bases upon which we collect and use your personal data, please contact our
      {' Support Team '}
      <a
        href={SUPPORT_URLS.REQUEST}
        className={styles.link}
        {...NEW_TAB_ATTRIBUTES}
      >
        here
      </a>
      .
    </p>
    <h5>Digest</h5>
    <p className={styles.digest}>
      We have lawful bases to collect, use and share data about you. You have choices about our use
      of your data.
    </p>

    <h3>Interest-Based Advertising</h3>
    <p>
      Interest-based advertising is advertising that is targeted to you based on your web browsing
      and app usage over time and across websites or apps. You have the option to restrict the use
      of information for interest-based advertising and to opt-out of receiving interest-based ads.
      Depending on from where you access our Site, you may also be asked to consent to our use of
      cookies, including advertising cookies.
    </p>
    <p>
      The opt-outs for interest-based advertising require that strictly necessary cookies are not
      blocked by the settings in your web browser. We support the Self-Regulatory Principles for
      {' Online Behavioral Advertising of the '}
      <a
        href="http://www.aboutads.info/"
        className={styles.link}
        {...NEW_TAB_ATTRIBUTES}
      >
        Digital Advertising Alliance
      </a>
      {' in the U.S., the '}
      <a
        href="https://youradchoices.ca/"
        className={styles.link}
        {...NEW_TAB_ATTRIBUTES}
      >
        Canadian Digital Advertising Alliance
      </a>
      {' in Canada, and the '}
      <a
        href="http://www.edaa.eu/European-principles/"
        className={styles.link}
        {...NEW_TAB_ATTRIBUTES}
      >
        European Digital Advertising Alliance
      </a>
      {' in the EU. '}
      If you live in the United States, Canada, or the European Union,
      {' '}
      {'you can visit '}
      <a
        href="http://optout.aboutads.info/"
        className={styles.link}
        {...NEW_TAB_ATTRIBUTES}
      >
        Ad Choices
      </a>
      {' (US), '}
      <a
        href="http://youradchoices.ca/choices/"
        className={styles.link}
        {...NEW_TAB_ATTRIBUTES}
      >
        Your Ad Choices
      </a>
      {' (Canada), or '}
      <a
        href="http://www.youronlinechoices.com/"
        className={styles.link}
        {...NEW_TAB_ATTRIBUTES}
      >
        Your Online Choices
      </a>
      {' (EU) '}
      to find a convenient place to opt-out of interest-based advertising with participating
      entities for each region. These websites also provide detailed information about how
      interest-based advertising works. Opting out does not mean that you will no longer receive
      advertising from us, or when you use the Internet. It just means that the data collected from
      our website will not be used for interest-based advertising and that the advertising you see
      displayed on websites will not be customized to your interests.
    </p>

    <h3>Social Network Widgets</h3>
    <p>
      Our Site may include social network sharing widgets that may provide information to their
      associated social networks or third-parties about your interactions with our web pages that
      you visit, even if you do not click on or otherwise interact with the plug-in or widget.
      Information is transmitted from your browser and may include an identifier assigned by the
      social network or third party, information about your browser type, operating system, device
      type, IP address, and the URL of the web page where widget appears. If you use social network
      tools or visit social networking sites, you should read their privacy disclosures, to learn
      what information they collect, use, and share.
    </p>

    <h3>Third-Party Direct Marketing</h3>
    <p>We do not share personal data with third parties for their direct marketing purposes.</p>

    <h3>California Residents Only</h3>
    <p>
      California law (CCPA) permits California residents to request, free of charge, personal
      information that is held by Getmyboat, Inc. California residents may make such a request in
      writing by emailing
      {' '}
      <a
        href="mailto:dpo@getmyboat.com"
        className={styles.link}
        {...NEW_TAB_ATTRIBUTES}
      >
        dpo@getmyboat.com
      </a>
      with your name and noting CCPA in the subject line of your email, or by calling the Toll
      Free Number 1-844-982-1809. Within 30 days of receiving your request, we will reply with
      identification of the personal information in possession.
    </p>
    <p>
      The California “Shine the Light Law,” California Civil Code Section 1798.83, permits
      California residents to request and obtain once a year, free of charge, information about
      Personal Information (as defined by California law), if any, that we disclose to certain
      third parties for direct marketing purposes in the preceding calendar year. California
      residents may make such a request in writing by emailing
      {' '}
      <a
        href="mailto:dpo@getmyboat.com"
        className={styles.link}
        {...NEW_TAB_ATTRIBUTES}
      >
        dpo@getmyboat.com
      </a>
      {' '}
      with your name and address, and noting “California Shine the Light Request” in the subject
      line of the email. Within 30 days of receiving your request, and where required by law, we
      will send you a list of the categories of personal information disclosed for third-party
      direct marketing purposes during the immediately preceding calendar year, along with the
      names and addresses of these third parties.
    </p>

    <strong>Contact Information</strong>
    <br />
    The Designated Data Controller is:
    <br />
    Data Privacy Officer
    <br />
    <a
      href="mailto:dpo@getmyboat.com"
      className={styles.link}
      {...NEW_TAB_ATTRIBUTES}
    >
      dpo@getmyboat.com
    </a>
    <br />
    818-927-2148
    <br />

    <h3>Changes to this Statement</h3>
    <p>
      We will occasionally update this Privacy Statement to reflect company and customer
      feedback. We encourage you to periodically review this Statement to be informed of how
      we use your information.
    </p>

    <h3>Contact Information for Questions or Complaints</h3>
    <p>
      If you have questions or complaints regarding this Policy, please contact
      {' Getmyboat '}
      <a
        href={SUPPORT_URLS.REQUEST}
        className={styles.link}
        {...NEW_TAB_ATTRIBUTES}
      >
        online
      </a>
      . You can also reach us by phone (818-927-2148) email (
      <a
        href={`mailto:${GMB_SUPPORT_EMAIL}`}
        className={styles.link}
        {...NEW_TAB_ATTRIBUTES}
      >
        {GMB_SUPPORT_EMAIL}
      </a>
      ) and physical mail (Getmyboat, Inc. 101 International Parkway, Adairsville, Georgia 30103).
    </p>
    <p>
      If you notice any unlawful use of Personally Identifiable Information or if you believe that
      we have not adhered to this Statement, we would be grateful if you would notify us by Toll
      Free Number 1-844-982-1809 or email to
      {' '}
      <a
        href="mailto:dpo@getmyboat.com"
        className={styles.link}
        {...NEW_TAB_ATTRIBUTES}
      >
        dpo@getmyboat.com
      </a>
      . You may also have a right to lodge
      a complaint with a supervisory authority.
    </p>
    <h5>Digest</h5>
    <p className={styles.digest}>
      You can contact us or use other options to resolve any complaints.
    </p>
  </main>
);

export default PrivacyPolicy;
